// src/pages/ChatPagemap.jsx
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  doc,
  updateDoc,
  serverTimestamp,
  addDoc,
  collection,
  arrayUnion,
  getDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import {
  subscribeNodes,
  voteNode,
  verifyStoryPassword,
} from "../modules/firebaseAPI";
import "./ChatPage.css";
import ChatPagemapSidebar from "../components/Sidebar/ChatPagemapSidebar";
import Tree from "react-d3-tree";
import { useLinkCreator } from "../modules/Button/LinkCreator";
import { useLinkRemover } from "../modules/Button/LinkRemover";
import "../modules/Button/Button.css";
import CommentSection from "../modules/Chat/CommentSection";
import PasswordPrompt from "../modules/PasswordPrompt"; // 🔒 パスワードモジュールを追加
import AffiliateLinks from "../modules/amazonAffiliate/AffiliateLinks";
import Seo from "../components/Seo";

// ---------------------
// ツリー構築関数
// ---------------------
export const buildTreeFromNodes = (nodes, parentStory) => {
  if (!nodes || nodes.length === 0) return { name: "ROOT", children: [] };
  const rootNode = nodes.find((n) => n.content || n.text);
  if (!rootNode) return { name: "ROOT", children: [] };

  const nodeMap = {};
  nodes.forEach((n) => {
    if (!n.id) return;
    nodeMap[n.id] = {
      id: n.id,
      name: n.text || n.content || "無題",
      children: [],
      parentIdForClick: n.parentId || null,
      firstNodeId: parentStory?.firstNodeId || null,
      parentNodeId: parentStory?.parentNodeId || null,
      storyId: parentStory?.id || null,
    };
  });

  nodes.forEach((n) => {
    if (Array.isArray(n.childrenIds)) {
      n.childrenIds.forEach((childId) => {
        if (nodeMap[childId]) {
          nodeMap[n.id].children.push(nodeMap[childId]);
        }
      });
    }
  });

  return {
    id: rootNode.id,
    name: rootNode.content || rootNode.text,
    children: nodeMap[rootNode.id]?.children || [],
    parentIdForClick: rootNode.parentId || null,
    firstNodeId: parentStory?.firstNodeId || null,
    parentNodeId: parentStory?.parentNodeId || null,
    storyId: parentStory?.id || null,
  };
};

// ---------------------
// 投票設定コンポーネント
// ---------------------
const VoteSettings = ({ story, userId, setStory }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newVoteMethod, setNewVoteMethod] = useState(story.voteMethod);
  const [newVoteTime, setNewVoteTime] = useState(story.voteTime);

  const handleSave = async () => {
    const storyRef = doc(db, "stories", story.id);
    try {
      await updateDoc(storyRef, {
        voteMethod: newVoteMethod,
        voteTime: Number(newVoteTime),
        updatedAt: serverTimestamp(),
      });
      setStory((prev) => ({
        ...prev,
        voteMethod: newVoteMethod,
        voteTime: Number(newVoteTime),
      }));
      setIsEditing(false);
    } catch (err) {
      console.error("更新失敗:", err);
    }
  };

  if (story.createdBy !== userId) {
    return (
      <div>
        <p>
          <strong>投票方法:</strong> {story.voteMethod}
        </p>
        <p>
          <strong>投票時間:</strong> {story.voteTime}分
        </p>
      </div>
    );
  }

  return (
    <div>
      {!isEditing ? (
        <>
          <p>
            <strong>投票方法:</strong>{" "}
            {(() => {
              switch (story.voteMethod) {
                case "author":
                  return "スレ主が選ぶ";
                case "timed":
                  return "ランダム採用";
                case "vote":
                  return "多数決";
                default:
                  return "不明";
              }
            })()}
          </p>
          {newVoteMethod !== "author" && (
            <p>
              <strong>投票時間:</strong> {story.voteTime}分
            </p>
          )}
          <button onClick={() => setIsEditing(true)}>
            スレットの設定を変更
          </button>
        </>
      ) : (
        <div>
          <label>
            投票方法:
            <select
              value={newVoteMethod}
              onChange={(e) => setNewVoteMethod(e.target.value)}
            >
              <option value="author">スレ主が選ぶ</option>
              <option value="timed">ランダム採用</option>
              <option value="vote">多数決</option>
            </select>
          </label>
          <br />
          {newVoteMethod !== "author" && (
            <label>
              投票時間(分):
              <input
                type="number"
                value={newVoteTime}
                onChange={(e) => setNewVoteTime(e.target.value)}
                min={1}
              />
            </label>
          )}
          <br />
          <button onClick={handleSave}>保存</button>
          <button onClick={() => setIsEditing(false)}>キャンセル</button>
        </div>
      )}
    </div>
  );
};

// ---------------------
// メインラッパー
// ---------------------
export default function ChatPagemapWrapper({ userId }) {
  const { storyId: paramStoryId, nodeId: paramNodeId } = useParams();
  const location = useLocation();
  const storyId = paramStoryId || location.state?.storyId;
  const nodeId = paramNodeId || location.state?.nodeId;

  const [nodes, setNodes] = useState([]);
  const [nodeData, setNodeData] = useState(null);
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // --- Firestore購読 ---
  useEffect(() => {
    if (!storyId) return;
    const unsubscribe = subscribeNodes(storyId, setNodes);
    return () => unsubscribe();
  }, [storyId]);

  // --- スレッド情報取得 ---
  useEffect(() => {
    if (!storyId || !nodeId) return;
    const fetchData = async () => {
      try {
        const { getNode, getThread } = await import("../modules/firebaseAPI");
        const [node, fetchedStory] = await Promise.all([
          getNode(storyId, nodeId),
          getThread(storyId),
        ]);
        setNodeData(node);
        setStory(fetchedStory);

        // 🔒 ローカルで解除済みチェック
        const unlockedStories = JSON.parse(
          localStorage.getItem("unlockedStories") || "{}"
        );
        if (!fetchedStory.password || unlockedStories[storyId]) {
          setUnlocked(true);
        }
      } catch (err) {
        console.error("データ取得エラー:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storyId, nodeId]);

  // --- パスワード解除後に保存 ---
  const handleUnlock = () => {
    setUnlocked(true);
    const unlockedStories = JSON.parse(
      localStorage.getItem("unlockedStories") || "{}"
    );
    localStorage.setItem(
      "unlockedStories",
      JSON.stringify({ ...unlockedStories, [storyId]: true })
    );
  };

  // --- 初期ノード登録 ---
  useEffect(() => {
    if (story && nodeData && !story.currentNodeContent) {
      const storyRef = doc(db, "stories", story.id);
      updateDoc(storyRef, {
        currentNodeContent: nodeData.content,
        updatedAt: serverTimestamp(),
      })
        .then(() =>
          setStory((prev) => ({
            ...prev,
            currentNodeContent: nodeData.content,
          }))
        )
        .catch((err) => console.error("開始文の初期化に失敗:", err));
    }
  }, [story, nodeData]);

  const handleAddNode = async (parentId = null) => {
    try {
      await addDoc(collection(db, "stories", storyId, "nodes"), {
        text: "新しい選択肢",
        parentId: parentId ?? null,
        round: 1,
        status: [],
      });
    } catch (err) {
      console.error("ノード追加失敗:", err);
    }
  };

  const handleVote = async (nodeId) => {
    if (!story) return;
    await voteNode(story.id, nodeId, userId);
  };

  if (loading) return <div>チャット読み込み中...</div>;
  if (!nodeData || !story) return <div>データが存在しません</div>;

  // 🔒 パスワード未解除の場合
  if (!unlocked && story.password) {
    return (
      <PasswordPrompt
        onSubmit={async (inputPassword) => {
          const isValid = await verifyStoryPassword(story.id, inputPassword);
          if (isValid) handleUnlock();
          else alert("パスワードが違います");
        }}
      />
    );
  }

  // ✅ フェードイン付きのページ本体
  return (
    <div className="fade-in" style={{ display: "flex" }}>
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? "×" : "選択した本文を見る"}
      </button>

      <ChatPagemap
        userId={userId}
        currentNode={nodeData}
        story={story}
        setStory={setStory}
        nodes={nodes}
        setNodes={setNodes}
      />
      <ChatPagemapSidebar
        className={`chat-sidebar ${sidebarOpen ? "open" : ""}`}
        currentNode={nodeData}
        nodes={nodes}
        onAddNode={handleAddNode}
        onVoteNode={handleVote}
        onResetNodes={() => setNodes([])}
        userId={userId}
        story={story}
        setStory={setStory}
        voteTime={story.voteTime}
        voteMethod={story.voteMethod}
      />
    </div>
  );
}

// ---------------------
// チャットマップコンポーネント
// ---------------------
export const ChatPagemap = ({
  userId,
  story,
  currentNode,
  setStory,
  nodes,
  setNodes,
}) => {
  const { nodeId } = useParams();

  const {
    linkMode,
    selectedNode: selectedLinkNode,
    toggleLinkMode,
    handleNodeClick: handleLinkClick,
    errorMessage: linkErrorMessage,
  } = useLinkCreator(story.id, nodes, setNodes);

  const {
    removeMode,
    selectedRemoveNode,
    handleNodeClick: handleRemoveClick,
    toggleRemoveMode,
    errorMessage: removeErrorMessage,
  } = useLinkRemover(story.id, nodes);

  const handleAddChild = async (clickedNode) => {
    const text = prompt("新しい選択肢を入力してください");
    if (!text) return;

    try {
      const firstNodeId = clickedNode.firstNodeId || null;
      const parentNodeId = clickedNode.parentNodeId || null;

      const newParentId =
        clickedNode.id === firstNodeId
          ? [parentNodeId, firstNodeId]
          : [clickedNode.id];

      const newNodeObj = {
        text,
        parentId: newParentId,
        round: story.currentRound || 1,
        votes: 0,
        voters: [],
        createdAt: new Date(),
        createdBy: userId,
        status: [],
        childrenIds: [],
      };

      const newNodeRef = await addDoc(
        collection(db, "stories", story.id, "nodes"),
        newNodeObj
      );

      const candidateTargetIds =
        clickedNode.id === firstNodeId
          ? [firstNodeId, parentNodeId].filter(Boolean)
          : [clickedNode.id];

      for (const targetId of candidateTargetIds) {
        const parentNodeRef = doc(db, "stories", story.id, "nodes", targetId);
        const snap = await getDoc(parentNodeRef);
        if (!snap.exists()) continue;

        await updateDoc(parentNodeRef, {
          childrenIds: arrayUnion(newNodeRef.id),
        });
      }

      setNodes((prev) => [...prev, { id: newNodeRef.id, ...newNodeObj }]);
    } catch (err) {
      console.error("子ノード追加失敗:", err);
    }
  };

  const renderCustomNode = ({ nodeDatum }) => {
    const isSelected = selectedLinkNode?.id === nodeDatum.id;
    const isSelectedForRemove = selectedRemoveNode?.id === nodeDatum.id;

    let nodeClass = "";
    if (isSelected) nodeClass = "highlight-node";
    else if (isSelectedForRemove) nodeClass = "highlight-remove-node";
    else if (linkMode) nodeClass = "link-mode-node";
    else if (removeMode) nodeClass = "remove-mode-node";
    else nodeClass = "normal-node";

    return (
      <g>
        <circle
          r={15}
          className={nodeClass}
          style={{ cursor: "pointer" }}
          onClick={() => {
            if (linkMode) return handleLinkClick(nodeDatum);
            if (removeMode) return handleRemoveClick(nodeDatum);
            setStory((prev) => ({
              ...prev,
              currentNodeContent: nodeDatum.name,
            }));
          }}
        />
        <text x={-10} dy={-30}>
          {nodeDatum.name}
        </text>
        <text
          x={-10}
          y={30}
          style={{ cursor: "pointer", fill: "green" }}
          onClick={() => handleAddChild(nodeDatum)}
        >
          ＋
        </text>
      </g>
    );
  };

  const treeData = buildTreeFromNodes(nodes, story);

  return (
    <>
      {/* 🔹 SEO タグ */}
      <Seo
        title={story.title}
        description={story.title}
        image="/siteicon.PNG"
        url={`https://mind-threat.site/stories/${story.id}`}
        type="article"
      />
      <div className="chat-container">
        <div className="thread-info">
          <h1>{story.title}</h1>
          <p>
            <strong>開始文:</strong> {currentNode.content}
          </p>
          <p>
            <strong>カテゴリ:</strong> {story.category}
          </p>
          <p>
            <strong>スレッドタイプ:</strong>{" "}
            {(() => {
              switch (story.threadType) {
                case "itinerary":
                  return "マインドマップ型";
                case "story":
                  return "ストーリー型";
                default:
                  return story.threadType; // 万が一知らない値の場合はそのまま表示
              }
            })()}
          </p>
          <VoteSettings story={story} userId={userId} setStory={setStory} />
        </div>

        <div style={{ width: "100%", height: "400px" }}>
          <Tree
            data={treeData}
            orientation="horizontal"
            renderCustomNodeElement={renderCustomNode}
            translate={{ x: 100, y: 200 }}
          />
        </div>

        <div style={{ marginTop: "30px", marginBottom: "10px" }}>
          <button
            onClick={toggleLinkMode}
            style={{
              backgroundColor: linkMode ? "#f87171" : "#60a5fa",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "8px 16px",
              cursor: "pointer",
            }}
          >
            {linkMode ? "リンク作成キャンセル" : "リンクモード開始"}
          </button>
          {linkErrorMessage && (
            <div style={{ color: "red", marginTop: "8px" }}>
              {linkErrorMessage}
            </div>
          )}

          <button
            onClick={toggleRemoveMode}
            style={{
              backgroundColor: removeMode ? "#f87171" : "#60a5fa",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "8px 16px",
              cursor: "pointer",
              marginLeft: "8px",
            }}
          >
            {removeMode ? "リンク削除キャンセル" : "リンク削除モード開始"}
          </button>
          {removeErrorMessage && (
            <div style={{ color: "red", marginTop: "8px" }}>
              {removeErrorMessage}
            </div>
          )}
        </div>

        <div className="affiliate-group home-section">
          {/* 🔹 AffiliateLinks を表示 */}
          <AffiliateLinks />
        </div>

        <CommentSection nodeId={nodeId} userId={userId} />
      </div>
    </>
  );
};
